import React, { useEffect, useState, useRef } from "react";
import { Row, Col, Button } from "reactstrap";
import Dropzone from "react-dropzone";
import { get, post, put, uploadImg } from 'helpers/api_helper';
import { PATIENT_URL } from 'helpers/url_helper';
import cookieHelper from "helpers/getCookieData";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Select from 'react-select';
import { AvForm, AvField } from 'availity-reactstrap-validation';
import { showSuccessAlert, showErroeAlert } from "pages/utils/alertMessages";
import { encodePatientId } from "helpers/url_crypto_helper";
import CTMTabs from "./CTMTabs";
import { 
  RiUserHeartLine, 
  RiShieldCheckLine, 
  RiMapPinLine, 
  RiUserVoiceLine,
  RiAddLine,
  RiCameraLine,
  RiCheckLine,
  RiCloseLine
} from "react-icons/ri";

const AddPatient = ({ patientData, is_update = false, onSuccess, onCancel }) => {
    const { t } = useTranslation();
    const clinic = cookieHelper.getCookie('_c') ? JSON.parse(atob(cookieHelper.getCookie('_c'))) : null;
    const debounceTimeout = useRef(null);
    const history = useHistory();
    const [loading, setLoading] = useState(false);

    const [formData, setFormData] = useState({
        title: "Mr.",
        communication_group: patientData?.communication_group || "[]",
        patient_tags: patientData?.patient_tags || "[]",
        allergies: patientData?.allergies || "[]",
        country: 'India',
        ...patientData
    });

    const [date, setDate] = useState(patientData?.date || new Date().toISOString().split("T")[0]);
    const [gender, setGender] = useState(patientData?.gender || "Male");
    const [dob, setDob] = useState(patientData?.dob || "");
    const [age, setAge] = useState(patientData?.age ? `${patientData.age} years` : "");
    const [referanceOptions, setReferanceOptions] = useState([]);
    const [options, setOptions] = useState({ communicationGroup: [], tags: [], allergies: [], language: [] });
    const [modalOpen, setModalOpen] = useState(false);
    const [modalType, setModalType] = useState("CommunicationGroup");

    const openModal = (type) => {
        setModalType(type);
        setModalOpen(true);
    };

    const closeModal = () => setModalOpen(false);

    const optionsFatch = async () => {
        try {
            const { success, body } = await get(`${PATIENT_URL}/profile/options`);
            if (success && body) {
                sessionStorage.setItem('options', JSON.stringify(body));
                setOptions(body);
            }
        } catch (e) {
            console.error(e);
        }
    };

    useEffect(() => {
        optionsFatch();
    }, []);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const calculateAge = (dateString) => {
        if (!dateString) return "";
        const dobDate = new Date(dateString);
        const now = new Date();
        let years = now.getFullYear() - dobDate.getFullYear();
        let months = now.getMonth() - dobDate.getMonth();

        if (months < 0) {
            years--;
            months += 12;
        }
        return `${years} years ${months} months`;
    };

    const handleDobChange = (e) => {
        const selectedDate = e.target.value;
        setDob(selectedDate);
        setAge(calculateAge(selectedDate));
    };

    const handleAcceptedFiles = async (files) => {
        if (!files || files.length === 0) return;
        const data = new FormData();
        data.append('file_type', 'profile');
        data.append('files[]', files[0]);

        try {
            if (patientData?.id) {
                const { success, body } = await uploadImg(`${PATIENT_URL}/profile?patient_id=${patientData.id}`, data);
                if (success) {
                    showSuccessAlert('Profile image uploaded successfully.');
                    setFormData(prev => ({ ...prev, profile_pic: body?.profile_pic }));
                }
            } else {
                // Temporary preview for new registration
                const reader = new FileReader();
                reader.onload = () => {
                    setFormData(prev => ({ ...prev, profile_preview: reader.result }));
                };
                reader.readAsDataURL(files[0]);
            }
        } catch (error) {
            showErroeAlert('Error uploading profile picture');
        }
    };

    const handleInputChange = (inputValue) => {
        setReferanceOptions([]);
        if (debounceTimeout.current) clearTimeout(debounceTimeout.current);

        debounceTimeout.current = setTimeout(() => {
            if (!inputValue) return;
            const type = (formData?.reference_type || '').toLowerCase();
            get(`${PATIENT_URL}/options?_type=${type}s&search=${inputValue}`)
                .then(({ success, body }) => {
                    if (success) {
                        const opts = Array.isArray(body) ? body : [];
                        setReferanceOptions(opts);
                    }
                });
        }, 500);
    };

    const handleSubmit = async () => {
        setLoading(true);
        const payload = {
            ...formData,
            date,
            gender,
            dob,
            age: parseInt(age) || formData?.age || 0,
            clinic_id: clinic?.id || 8,
            client_id: clinic?.client_id || 71,
            doctor: formData?.doctor || 'Doc-1'
        };

        try {
            const res = is_update && patientData?.id
                ? await put(`${PATIENT_URL}/${patientData.id}`, payload)
                : await post(PATIENT_URL, payload);

            if (res?.success) {
                showSuccessAlert(is_update ? "Patient details updated successfully!" : "Patient registered successfully!");
                if (onSuccess) {
                    onSuccess(res.body);
                } else if (res.body?.id) {
                    history.push({
                        pathname: '/patient',
                        search: `?_p=${encodePatientId(res.body.id)}`,
                        state: { patientData: res.body }
                    });
                }
            } else {
                showErroeAlert(res?.message || "Failed to save patient record. Please check the fields.");
            }
        } catch (err) {
            console.error(err);
            showErroeAlert("An error occurred while saving patient record.");
        } finally {
            setLoading(false);
        }
    };

    const handleInvalidSubmit = () => {
        showErroeAlert("Please fill in all mandatory fields marked with (*).");
    };

    // Helper for multi-select arrays
    const parseMultiSelect = (val) => {
        if (!val) return [];
        if (Array.isArray(val)) return val;
        try {
            const parsed = JSON.parse(val);
            return Array.isArray(parsed) ? parsed : [];
        } catch (e) {
            return val.split(',').map(s => s.trim()).filter(Boolean);
        }
    };

    return (
        <AvForm onValidSubmit={handleSubmit} onInvalidSubmit={handleInvalidSubmit} model={formData} className="patient-form-wrapper">
            {/* Section 1: Basic Demographics & Registration */}
            <div className="patient-form-section-card">
                <div className="section-header">
                    <h6 className="section-title">
                        <RiUserHeartLine className="text-primary" />
                        {t("Dental Patients Directory & EMR Registry") || "Patient Registration & Demographics"}
                    </h6>
                    <span className="badge bg-soft-primary text-primary border border-primary font-size-11 px-2 py-1">
                        Clinic ID: {clinic?.id || 8}
                    </span>
                </div>

                <Row className="g-3 align-items-center">
                    <Col md={2} className="text-center border-end pe-3">
                        <Dropzone multiple={false} onDrop={handleAcceptedFiles} accept="image/*">
                            {({ getRootProps, getInputProps }) => (
                                <div
                                    {...getRootProps()}
                                    className="patient-photo-dropzone d-flex flex-column align-items-center justify-content-center"
                                    title="Click or drag to upload patient photo"
                                >
                                    <input {...getInputProps()} />
                                    {formData?.profile_pic || formData?.profile_preview ? (
                                        <img
                                            src={formData?.profile_preview || `${process.env.REACT_APP_API_URL}${formData?.profile_pic}`}
                                            alt="Patient"
                                        />
                                    ) : (
                                        <div className="text-muted text-center p-1">
                                            <RiCameraLine className="font-size-22 text-primary d-block mx-auto mb-0.5" />
                                            <span className="font-size-10 d-block font-weight-semibold">{t("Icons") || "Photo"}</span>
                                        </div>
                                    )}
                                </div>
                            )}
                        </Dropzone>
                        <span className="text-muted font-size-11 font-weight-semibold d-block mt-2">{t("Icons") || "Upload Photo"}</span>
                    </Col>

                    <Col md={10}>
                        <Row className="g-3">
                            <Col md={4}>
                                <label className="form-label">{t("Registration Date")} *</label>
                                <input
                                    type="date"
                                    name="date"
                                    className="form-control"
                                    value={date}
                                    onChange={(e) => setDate(e.target.value)}
                                    required
                                />
                            </Col>

                            <Col md={4}>
                                <label className="form-label">{t("Case Number")} / UHID *</label>
                                <AvField
                                    name="case_no"
                                    type="text"
                                    placeholder="e.g. 909-P-101"
                                    value={formData?.case_no || ''}
                                    onChange={handleChange}
                                    required
                                    errorMessage="Please enter Case Number"
                                />
                            </Col>

                            <Col md={4}>
                                <label className="form-label">{t("Gender")} *</label>
                                <div className="gender-btn-group">
                                    <button
                                        type="button"
                                        className={`gender-pill ${gender === "Male" ? "active" : ""}`}
                                        onClick={() => setGender("Male")}
                                    >
                                        👨 {t("Male")}
                                    </button>
                                    <button
                                        type="button"
                                        className={`gender-pill ${gender === "Female" ? "active" : ""}`}
                                        onClick={() => setGender("Female")}
                                    >
                                        👩 {t("Female")}
                                    </button>
                                    <button
                                        type="button"
                                        className={`gender-pill ${gender === "Other" ? "active" : ""}`}
                                        onClick={() => setGender("Other")}
                                    >
                                        ⚧ {t("Other")}
                                    </button>
                                </div>
                            </Col>

                            <Col md={2}>
                                <label className="form-label">{t("Profile") || "Title"}</label>
                                <select
                                    className="form-select"
                                    name="title"
                                    value={formData?.title || "Mr."}
                                    onChange={handleChange}
                                >
                                    <option value="Mr.">Mr.</option>
                                    <option value="Mrs.">Mrs.</option>
                                    <option value="Miss.">Miss.</option>
                                    <option value="Dr.">Dr.</option>
                                    <option value="Master">Master</option>
                                </select>
                            </Col>

                            <Col md={5}>
                                <label className="form-label">{t("First Name")} *</label>
                                <AvField
                                    name="first_name"
                                    type="text"
                                    placeholder="Enter First Name"
                                    value={formData?.first_name || ''}
                                    onChange={handleChange}
                                    required
                                    errorMessage="First name is required"
                                />
                            </Col>

                            <Col md={5}>
                                <label className="form-label">{t("Last Name")}</label>
                                <AvField
                                    name="last_name"
                                    type="text"
                                    placeholder="Enter Last Name"
                                    value={formData?.last_name || ''}
                                    onChange={handleChange}
                                />
                            </Col>
                        </Row>
                    </Col>
                </Row>
            </div>

            {/* Section 2: Contact, Address & Location */}
            <div className="patient-form-section-card">
                <div className="section-header">
                    <h6 className="section-title">
                        <RiMapPinLine className="text-danger" />
                        {t("Address") || "Contact Information & Residential Address"}
                    </h6>
                </div>

                <Row className="g-3">
                    <Col md={4}>
                        <label className="form-label">{t("Mobile Phone Number")} *</label>
                        <AvField
                            name="mobile"
                            type="text"
                            placeholder="e.g. 9876543210"
                            value={formData?.mobile || ''}
                            onChange={handleChange}
                            required
                            errorMessage="Mobile number is required"
                        />
                    </Col>

                    <Col md={4}>
                        <label className="form-label">{t("Email Address")}</label>
                        <AvField
                            name="email"
                            type="email"
                            placeholder="e.g. patient@gmail.com"
                            value={formData?.email || ''}
                            onChange={handleChange}
                        />
                    </Col>

                    <Col md={4}>
                        <label className="form-label">{t("Mobile Phone Number")} (Alt)</label>
                        <input
                            type="text"
                            name="alternative_mobile"
                            className="form-control"
                            placeholder="e.g. 9123456780"
                            value={formData?.alternative_mobile || ''}
                            onChange={handleChange}
                        />
                    </Col>

                    <Col md={3}>
                        <label className="form-label">
                            {t("Date of Birth")}
                            {age && <span className="badge bg-soft-primary text-primary font-size-10">{age}</span>}
                        </label>
                        <input
                            type="date"
                            className="form-control"
                            value={dob}
                            onChange={handleDobChange}
                        />
                    </Col>

                    <Col md={5}>
                        <label className="form-label">{t("Address")}</label>
                        <input
                            type="text"
                            name="address"
                            className="form-control"
                            placeholder="Street, Flat/Building, Area"
                            value={formData?.address || ''}
                            onChange={handleChange}
                        />
                    </Col>

                    <Col md={2}>
                        <label className="form-label">{t("City")}</label>
                        <input
                            type="text"
                            name="city"
                            className="form-control"
                            placeholder="City"
                            value={formData?.city || ''}
                            onChange={handleChange}
                        />
                    </Col>

                    <Col md={2}>
                        <label className="form-label">{t("Pin Code")}</label>
                        <input
                            type="text"
                            name="zip_code"
                            className="form-control"
                            placeholder="Zip Code"
                            value={formData?.zip_code || ''}
                            onChange={handleChange}
                        />
                    </Col>
                </Row>
            </div>

            {/* Section 3: Clinical Alerts, Tags & Communication Profile */}
            <div className="patient-form-section-card">
                <div className="section-header">
                    <h6 className="section-title">
                        <RiShieldCheckLine className="text-warning" />
                        {t("Medical History") || "Clinical Profile, Medical Alerts & Tags"}
                    </h6>
                </div>

                <Row className="g-3">
                    <Col md={3}>
                        <label className="form-label">{t("Settings") || "Preferred Language"}</label>
                        <select
                            className="form-select"
                            name="language"
                            value={formData?.language || 'English'}
                            onChange={handleChange}
                        >
                            <option value="English">English</option>
                            <option value="Hindi">Hindi</option>
                            <option value="Marathi">Marathi</option>
                            <option value="Gujarati">Gujarati</option>
                            {(options?.language || []).map((v, i) => (
                                <option key={`lang-${i}`} value={v.value}>{v.label}</option>
                            ))}
                        </select>
                    </Col>

                    <Col md={3}>
                        <label className="form-label">
                            {t("Allergies")}
                            <button
                                type="button"
                                className="btn btn-sm btn-link p-0 text-primary font-size-11 font-weight-bold text-decoration-none"
                                onClick={() => openModal("Allergy")}
                            >
                                + {t("New")}
                            </button>
                        </label>
                        <Select
                            isMulti
                            className="basic-multi-select"
                            classNamePrefix="select"
                            options={options?.allergies || []}
                            value={(options?.allergies || []).filter(o => parseMultiSelect(formData?.allergies).includes(o.value))}
                            onChange={(selected) => {
                                const vals = (selected || []).map(s => s.value);
                                setFormData(prev => ({ ...prev, allergies: JSON.stringify(vals) }));
                            }}
                            placeholder="Select allergies..."
                        />
                    </Col>

                    <Col md={3}>
                        <label className="form-label">
                            {t("Primary Symptoms")}
                            <button
                                type="button"
                                className="btn btn-sm btn-link p-0 text-primary font-size-11 font-weight-bold text-decoration-none"
                                onClick={() => openModal("Tags")}
                            >
                                + {t("New")}
                            </button>
                        </label>
                        <Select
                            isMulti
                            className="basic-multi-select"
                            classNamePrefix="select"
                            options={options?.tags || []}
                            value={(options?.tags || []).filter(o => parseMultiSelect(formData?.patient_tags).includes(o.value))}
                            onChange={(selected) => {
                                const vals = (selected || []).map(s => s.value);
                                setFormData(prev => ({ ...prev, patient_tags: JSON.stringify(vals) }));
                            }}
                            placeholder="Select tags..."
                        />
                    </Col>

                    <Col md={3}>
                        <label className="form-label">
                            {t("Communications & Reminders") || "Communication Group"}
                            <button
                                type="button"
                                className="btn btn-sm btn-link p-0 text-primary font-size-11 font-weight-bold text-decoration-none"
                                onClick={() => openModal("CommunicationGroup")}
                            >
                                + {t("New")}
                            </button>
                        </label>
                        <Select
                            isMulti
                            className="basic-multi-select"
                            classNamePrefix="select"
                            options={options?.communicationGroup || []}
                            value={(options?.communicationGroup || []).filter(o => parseMultiSelect(formData?.communication_group).includes(o.value))}
                            onChange={(selected) => {
                                const vals = (selected || []).map(s => s.value);
                                setFormData(prev => ({ ...prev, communication_group: JSON.stringify(vals) }));
                            }}
                            placeholder="Select group..."
                        />
                    </Col>
                </Row>
            </div>

            {/* Section 4: Referral & Acquisition Channel */}
            {!is_update && (
                <div className="patient-form-section-card">
                    <div className="section-header">
                        <h6 className="section-title">
                            <RiUserVoiceLine className="text-info" />
                            {t("Reason for Visit") || "Referral & Marketing Source"}
                        </h6>
                    </div>

                    <Row className="g-3">
                        <Col md={4}>
                            <label className="form-label">{t("Reason for Visit")}</label>
                            <select
                                className="form-select"
                                name="reference_type"
                                value={formData?.reference_type || "Direct Walk-in"}
                                onChange={handleChange}
                            >
                                <option value="Direct Walk-in">Direct Walk-in</option>
                                <option value="Doctor Referral">Doctor Referral</option>
                                <option value="Existing Patient">Existing Patient</option>
                                <option value="Google / Online">Google / Online</option>
                                <option value="Social Media">Social Media</option>
                                <option value="Insurance Panel">Insurance Panel</option>
                                <option value="Other">Other</option>
                            </select>
                        </Col>

                        <Col md={4}>
                            <label className="form-label">{t("Attending Doctor")}</label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Referrer Name / Details"
                                name="reference"
                                value={formData?.reference || ''}
                                onChange={handleChange}
                            />
                        </Col>

                        <Col md={4}>
                            <label className="form-label">{t("Patients")}</label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="e.g. Self, Parent, Spouse"
                                name="patient_relationship"
                                value={formData?.patient_relationship || 'Self'}
                                onChange={handleChange}
                            />
                        </Col>
                    </Row>
                </div>
            )}

            {/* Modal Footer Controls */}
            <div className="patient-form-footer-bar">
                <div className="d-flex align-items-center gap-2">
                    <div className="form-check mb-0">
                        <input
                            type="checkbox"
                            className="form-check-input"
                            id="welcome_sms_checkbox"
                            name="welcome_checkbox"
                            onChange={handleChange}
                        />
                        <label className="form-check-label font-size-13 font-weight-semibold text-dark cursor-pointer ms-1" htmlFor="welcome_sms_checkbox">
                            {t("WhatsApp Reminder") || "Send automated welcome SMS / WhatsApp notification"}
                        </label>
                    </div>
                </div>

                <div className="d-flex align-items-center gap-2">
                    {onCancel && (
                        <button
                            type="button"
                            className="btn btn-outline-secondary px-4 font-weight-semibold"
                            onClick={onCancel}
                        >
                            {t("Cancel")}
                        </button>
                    )}
                    <button
                        type="submit"
                        className="btn btn-primary font-weight-bold px-4 py-2 shadow-sm d-inline-flex align-items-center gap-1.5"
                        disabled={loading}
                    >
                        <RiCheckLine className="font-size-16" />
                        {loading ? t("Loading") : (is_update ? t("Save Changes") : t("Save Changes"))}
                    </button>
                </div>
            </div>

            {/* Custom Tag/Allergy Creation Dialog */}
            <CTMTabs
                isOpen={modalOpen}
                toggle={closeModal}
                type={modalType}
                onItemAdded={(newItem) => {
                    if (!newItem) return;
                    const newOption = {
                        value: newItem.id ?? newItem.value ?? newItem.title,
                        label: newItem.title ?? newItem.label ?? newItem.value,
                    };
                    setOptions(prev => {
                        const updated = { ...prev };
                        if (modalType === "CommunicationGroup") {
                            updated.communicationGroup = [...(updated.communicationGroup || []), newOption];
                            setFormData(p => ({ ...p, communication_group: JSON.stringify([...parseMultiSelect(p.communication_group), newOption.value]) }));
                        }
                        if (modalType === "Tags") {
                            updated.tags = [...(updated.tags || []), newOption];
                            setFormData(p => ({ ...p, patient_tags: JSON.stringify([...parseMultiSelect(p.patient_tags), newOption.value]) }));
                        }
                        if (modalType === "Allergy") {
                            updated.allergies = [...(updated.allergies || []), newOption];
                            setFormData(p => ({ ...p, allergies: JSON.stringify([...parseMultiSelect(p.allergies), newOption.value]) }));
                        }
                        return updated;
                    });
                }}
            />
        </AvForm>
    );
};

export default AddPatient;